<script lang="ts">
    import {clickOutside} from 'shared/svelte-actions';
    import {popOutAnim} from 'shared/animations';
    import type {ClientRectObject, VirtualElement} from '@floating-ui/core';
    import {isTouchDevice} from 'shared/utils/custom-utils';
    import {uuid} from 'shared/utils/custom-utils';
    import {fade} from 'svelte/transition';
    import {writable} from 'svelte/store';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {assertIsElement, isHtmlElement} from 'shared/utils/types-utils';

    export let id: string | null;
    export let additionalContextMenuClasses = '';
    export let additionalButtonClasses = '';
    export let disabled = false;
    export let wrapperAsButton = false;

    const contextMenuWrapperId = uuid();
    const fadeAnimParams = {duration: 250};

    let contextMenuShown = false;
    let previousActiveElement: Element | null = null;
    let contextElementRef: Element | null = null;
    let contextMenuOffsetX = 0;
    let contextMenuOffsetY = 0;
    let contextMenuRefreshKey = 0;

    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-start', 0));

    const handleContextMenu = (event: MouseEvent) => {
        if (disabled || isTouchDevice()) {
            return;
        }

        contextMenuOffsetX = event.x;
        contextMenuOffsetY = event.y;
        previousActiveElement = document.activeElement;
        contextMenuShown = true;
        assertIsElement(event.target);
        contextElementRef = event.target;

        contextMenuRefreshKey++;
    }

    const closeMenu = () => {
        contextMenuShown = false;
        contextElementRef = null;
        if (isHtmlElement(previousActiveElement)) {
            previousActiveElement.focus();
            previousActiveElement = null;
        }
    }

    $: getBoundingClientRect = (): ClientRectObject => {
        return {
            width: 0,
            height: 0,
            top: contextMenuOffsetY,
            bottom: contextMenuOffsetY,
            left: contextMenuOffsetX,
            right: contextMenuOffsetX,
            x: contextMenuOffsetX,
            y: contextMenuOffsetY
        }
    }

    const virtualElement = writable<VirtualElement>({getBoundingClientRect});
    $: virtualElement.set({getBoundingClientRect})

    floatingRef(virtualElement);
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element id={id}
                this={wrapperAsButton ? 'button' : 'div'}
                {...wrapperAsButton ? {type: 'button'} : {}}
                class="kp-context-menu-wrapper {additionalButtonClasses}"
                on:contextmenu|preventDefault={handleContextMenu}
                on:click>

    <slot open={!disabled && contextMenuShown}/>
</svelte:element>

{#key contextMenuRefreshKey}
    {#if $$slots['context-content'] && !disabled && contextMenuShown}
        <div aria-hidden="true"
             class="context-menu {additionalContextMenuClasses}"
             on:click|capture={closeMenu}
             use:floatingContent
             use:clickOutside={{eventsTypes: ['click', 'contextmenu']}}
             on:click-outside={closeMenu}
             out:popOutAnim={{key: `context-menu-${contextMenuWrapperId}-${contextMenuRefreshKey}`}}>

            <div class="context-menu-content-container" in:fade={fadeAnimParams}>
                <slot name="context-content" {contextElementRef}/>
            </div>
        </div>
    {/if}
{/key}

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    .kp-context-menu-wrapper {
        all: unset;
        width: 100%;
        display: flex;
        flex-direction: column;
        border-radius: @border-radius-default;
    }

    .context-menu {
        z-index: 100;
        width: 15em;
        border-radius: @border-radius-large;
        background-color: @themed-body-bg;
        border: 1px solid @themed-border-default;
        box-shadow: 0 6px 16px 0 rgba(0, 0, 0, 0.08);

        .context-menu-content-container {
            display: flex;
            flex-direction: column;
            gap: @spacing-s;
            padding: @spacing-sm @spacing-m;
        }
    }
</style>